import { Component, inject, signal, output, Input, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@dental-ui/shared/i18n';
import { ButtonComponent } from '../button/button.component';
import { ToastService } from '../toast/toast.service';
import { AttachmentApiService, FileTypeEnum, DocumentTypeEnum } from '@dental-ui/attachment-service';
import { AuthStateService } from '@dental-ui/auth';
import { finalize } from 'rxjs';

@Component({
  selector: 'lib-profile-avatar-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ButtonComponent],
  templateUrl: './profile-avatar-dialog.component.html',
  styleUrl: './profile-avatar-dialog.component.scss',
})
export class ProfileAvatarDialogComponent implements OnDestroy {
  // Generic Inputs
  @Input() patientId: string | null = null;
  @Input() mode: 'user' | 'patient' = 'user';

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;

  private readonly attachmentApi = inject(AttachmentApiService);
  private readonly authState = inject(AuthStateService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly closeDialog = output<void>();
  readonly saved = output<void>();

  readonly selectedFile = signal<File | null>(null);
  readonly previewUrl = signal<string | null>(null);
  readonly isUploading = signal<boolean>(false);
  readonly uploadProgress = signal<number>(0);
  readonly isCameraActive = signal<boolean>(false);
  private cameraStream: MediaStream | null = null;

  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      if (file.size > this.MAX_FILE_SIZE) {
        this.toastService.error(
          this.translateService.getText('MESSAGES.FILE_TOO_LARGE', '10MB') ||
          'File size exceeds the 10MB limit'
        );
        input.value = '';
        return;
      }

      this.selectedFile.set(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        this.previewUrl.set(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  onSave(): void {
    const file = this.selectedFile();

    if (!file) return;

    this.isUploading.set(true);
    this.uploadProgress.set(0);

    let attachmentId;
    let fileType: FileTypeEnum;
    let docType: DocumentTypeEnum;
    let description: string;

    if (this.mode === 'patient' && this.patientId) {
      attachmentId = {
        name: 'patientId',
        value: this.patientId,
      };
      fileType = FileTypeEnum.Patient;
      docType = DocumentTypeEnum.PictureInFace;
      description = 'Patient Profile Photo';
    } else {
      const user = this.authState.user();
      if (!user?.userId) {
        this.isUploading.set(false);
        return;
      }
      attachmentId = {
        name: 'userId',
        value: user.userId,
      };
      fileType = FileTypeEnum.UserProfile;
      docType = DocumentTypeEnum.PictureInFace;
      description = 'User Avatar';
    }

    this.attachmentApi.uploadFile(
      file,
      attachmentId,
      fileType,
      docType,
      false,
      true,
      description
    ).pipe(
      finalize(() => {
        this.isUploading.set(false);
      })
    ).subscribe({
      next: (response) => {
        if (response.success) {
          this.toastService.success(
            this.translateService.getText('MESSAGES.PROFILE_PHOTO_UPLOAD_SUCCESS') ||
            'Profile photo updated successfully'
          );

          if (this.mode === 'user') {
            // Refresh session to get new avatar URL
            const token = this.authState.token();
            if (token) {
              this.authState.refreshSession(token).subscribe(() => {
                this.saved.emit();
                this.onClose();
              });
            } else {
              this.saved.emit();
              this.onClose();
            }
          } else {
            // For patient, just emit saved and close
            this.saved.emit();
            this.onClose();
          }
        } else {
          this.toastService.error(
            response.message ||
            this.translateService.getText('MESSAGES.UPLOAD_ATTACHMENT_FAILED') ||
            'Failed to upload profile picture'
          );
        }
      },
      error: (error) => {
        console.error('Error uploading avatar:', error);
        this.toastService.error(
          this.translateService.getText('MESSAGES.UPLOAD_ATTACHMENT_FAILED') ||
          'An error occurred while uploading profile picture'
        );
      }
    });
  }

  async startCamera(): Promise<void> {
    try {
      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 512, height: 512 }
      });
      this.isCameraActive.set(true);
      this.selectedFile.set(null);
      this.previewUrl.set(null);

      // Wait for the video element to be rendered by @if
      setTimeout(() => {
        if (this.videoElement && this.cameraStream) {
          this.videoElement.nativeElement.srcObject = this.cameraStream;
        }
      }, 100);
    } catch (err) {
      console.error('Error accessing camera:', err);
      this.toastService.error('Could not access camera. Please check permissions.');
    }
  }

  stopCamera(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }
    this.isCameraActive.set(false);
  }

  capturePhoto(): void {
    const videoElement = this.videoElement?.nativeElement;
    if (!videoElement) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoElement, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'captured_photo.jpg', { type: 'image/jpeg' });
          this.selectedFile.set(file);
          this.previewUrl.set(canvas.toDataURL('image/jpeg'));
          this.stopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  onClose(): void {
    this.stopCamera();
    this.closeDialog.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
